import {
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  TextField,
  Divider,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useCourseSearch } from "hooks/useCourseSearch";
import { toast } from "react-toastify";
import { IoArrowBack } from "react-icons/io5";
import { modules } from "const/constants";

const steps = [
  "Basic Details",
  "Interested Services",
  "Upload Documents",
  "Course & Intake",
  "Review & Submit",
];

const ClientMultiStepForm = ({ refetchClients }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [{ token, pagination }, { refetchPaginatedClient }] = useKothar();

  const [formData, setFormData] = useState({
    // Step 1: Basic Details
    name: "",
    email: "",
    phone: "",
    address: "",
    emergencyContact: {
      name: "",
      phone: "",
      relation: "",
      email: "",
    },
    // Step 2: Interested Services
    interestedServices: [],
    // Step 3: Documents
    documents: [],
    // Step 4: Course & Intake (only if admission selected)
    course: null,
    intake: "",
    university: null,
    extraInformation: "",
    otherServices: "",
  });

  const {
    inputValue: courseInputValue,
    setInputValue: setCourseInputValue,
    debouncedSearch: courseDebouncedSearch,
    options: courseOptions,
    loading: courseLoading,
    listboxProps: courseListboxProps,
  } = useCourseSearch({ selectedCourse: formData.course });

  const handleNext = () => {
    // Validation for each step
    if (activeStep === 0) {
      // Validate basic details
      if (
        !formData.name ||
        !formData.email ||
        !formData.phone ||
        !formData.address ||
        !formData.emergencyContact.name ||
        !formData.emergencyContact.phone ||
        !formData.emergencyContact.relation
      ) {
        toast.error("Please fill in all basic details");
        return;
      }
    } else if (activeStep === 1) {
      // Validate interested services
      if (formData.interestedServices.length === 0) {
        toast.error("Please select at least one interested service");
        return;
      }
    } else if (activeStep === 2) {
      // Documents are optional, but we can validate if needed
      // For now, skip validation
    } else if (activeStep === 3) {
      // Validate course intake if admission is selected
      if (formData.interestedServices.includes("Admission")) {
        if (!formData.course) {
          toast.error("Please select course and intake for admission service");
          return;
        }
      }
    }

    if (activeStep === 3 && !formData.interestedServices.includes("Admission")) {
      // Skip course intake step if admission not selected
      setActiveStep(4);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 4 && !formData.interestedServices.includes("Admission")) {
      // Skip back to step 2 if admission not selected
      setActiveStep(2);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleServiceToggle = (service) => {
    setFormData((prev) => {
      const services = prev.interestedServices;
      if (services.includes(service)) {
        // Remove service and clear related data
        const newServices = services.filter((s) => s !== service);
        const newFormData = { ...prev, interestedServices: newServices };
        if (service === "Admission") {
          newFormData.course = null;
          newFormData.intake = "";
          newFormData.extraInformation = "";
          newFormData.university = null;
        }
        return newFormData;
      } else {
        return { ...prev, interestedServices: [...services, service] };
      }
    });
  };

  const postClientData = async (payload) => {
    const response = await axios.post(`${API_URL}/clients`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const { mutate: submitForm, isLoading: isSubmitting } = useMutation(postClientData, {
    onSuccess: () => {
      toast.success("Client added successfully");
      navigate("/admin/client");
      if (refetchClients) {
        if (pagination) {
          refetchPaginatedClient({
            ...pagination,
            page: pagination.pageIndex,
            size: pagination.pageSize,
          });
        } else {
          refetchPaginatedClient({
            pageIndex: 0,
            pageSize: 20,
          });
        }
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage ?? "Error submitting client data");
    },
  });

  const handleSubmit = () => {
    // Prepare payload for client
    const payload = {
      name: formData.name,
      email: formData.email,
      number: formData.phone,
      address: formData.address,
      emergencyContactName: formData.emergencyContact.name,
      emergencyContact: formData.emergencyContact.phone,
      emergencyRelation: formData.emergencyContact.relation,
      emergencyEmail: formData.emergencyContact.email || "",
      active: "Y",
      isConverted: false,
      interestedServices: formData.interestedServices,
      otherServices: formData.otherServices,
      documents: formData.documents,
      ...(formData.interestedServices.includes("Admission") && {
        courseId: formData.course?.courseId || formData.course?.id,
        intake: formData.intake,
        extraInformation: formData.extraInformation,
        courseName: formData.course?.courseName,
        currentCourseProvider: formData.university?.name || formData.course?.university?.name,
      }),
    };

    submitForm(payload);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box className="space-y-6 py-6">
            <Typography variant="h6" className="mb-4 text-app-text">
              Basic Information
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Full Name"
                placeholder="Enter Full Name"
                name="name"
                required
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
              <InputField
                type="email"
                placeholder="Email"
                name="email"
                label="Email"
                required
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              />
              <InputField
                type="text"
                placeholder="Phone Number"
                name="phone"
                label="Phone Number"
                required
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              />
              <InputField
                type="text"
                placeholder="Address"
                name="address"
                label="Address"
                required
                value={formData.address}
                onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <Divider className="my-6" />
            <Typography variant="h6" className="mb-4 text-app-text">
              Emergency Contact
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Contact Name"
                placeholder="Emergency Contact Name"
                name="emergencyContactName"
                required
                type="text"
                value={formData.emergencyContact.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emergencyContact: {
                      ...prev.emergencyContact,
                      name: e.target.value,
                    },
                  }))
                }
              />
              <InputField
                type="email"
                placeholder="Emergency Email"
                name="emergencyEmail"
                label="Emergency Email"
                value={formData.emergencyContact.email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emergencyContact: {
                      ...prev.emergencyContact,
                      email: e.target.value,
                    },
                  }))
                }
              />
              <InputField
                type="text"
                placeholder="Phone Number"
                name="emergencyContactPhone"
                label="Phone Number"
                required
                value={formData.emergencyContact.phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emergencyContact: {
                      ...prev.emergencyContact,
                      phone: e.target.value,
                    },
                  }))
                }
              />
              <InputField
                type="text"
                placeholder="Relation"
                name="emergencyContactRelation"
                label="Relation"
                required
                value={formData.emergencyContact.relation}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emergencyContact: {
                      ...prev.emergencyContact,
                      relation: e.target.value,
                    },
                  }))
                }
              />
            </div>
          </Box>
        );

      case 1:
        return (
          <Box className="space-y-6 py-6">
            <Typography variant="h6" className="mb-4 text-app-text">
              Select Interested Services
            </Typography>
            <Typography variant="body2" className="mb-6 text-app-text-secondary">
              Please select all services you are interested in
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {modules
                .filter((module) =>
                  [
                    "Visa",
                    "Admission",
                    "RPL",
                    "Skill Assessments",
                    "Consultation",
                    "Insurance",
                    "Other",
                  ].includes(module.value)
                )
                .map((module) => (
                  <Paper
                    key={module.value}
                    elevation={formData.interestedServices.includes(module.value) ? 7 : 1}
                    className={`p-4 cursor-pointer transition-all ${
                      formData.interestedServices.includes(module.value)
                        ? "border-2 border-primary bg-primary/5"
                        : "border-2 border-transparent hover:border-app-border"
                    }`}
                    onClick={() => handleServiceToggle(module.value)}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.interestedServices.includes(module.value)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceToggle(module.value);
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" className="font-semibold">
                          {module.label}
                        </Typography>
                      }
                    />
                  </Paper>
                ))}
            </div>
            {formData?.interestedServices?.includes("Other") && (
              <InputField
                label="Mention Other Services"
                placeholder="Enter Other Services"
                name="otherServices"
                type="text"
                value={formData.otherServices}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, otherServices: e.target.value }))
                }
              />
            )}
          </Box>
        );

      case 2:
        return (
          <Box className="space-y-6 py-6">
            <Typography variant="h6" className="mb-4 text-app-text">
              Upload Documents
            </Typography>
            <Typography variant="body2" className="mb-6 text-app-text-secondary">
              Upload relevant documents for the selected services like Visa, COE, USI, Insurance,
              Passport, etc.
            </Typography>
            <UploadFile
              data={formData}
              setData={(newData) => setFormData((prev) => ({ ...prev, ...newData }))}
              label="Documents"
              imageKey="documents"
              type="multiple"
            />
          </Box>
        );

      case 3:
        if (!formData.interestedServices.includes("Admission")) {
          return null;
        }
        return (
          <Box className="space-y-6 py-6">
            <Typography variant="h6" className="mb-4 text-app-text">
              Course & Intake Selection
            </Typography>
            <Typography variant="body2" className="mb-6 text-app-text-secondary">
              Please select the course and intake for admission
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Autocomplete
                disablePortal
                size="small"
                options={courseOptions}
                loading={courseLoading}
                filterOptions={(x) => x}
                value={formData.course || null}
                inputValue={courseInputValue}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === "reset") return;
                  setCourseInputValue(newInputValue);
                }}
                onChange={(e, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    course: newValue,
                    university: newValue?.university || null,
                  }));
                  if (newValue?.courseName) setCourseInputValue(newValue.courseName);
                }}
                getOptionLabel={(option) =>
                  `${option?.courseName || ""} - ${option?.university?.name || ""}`
                }
                isOptionEqualToValue={(option, value) =>
                  option?.courseId === value?.courseId || option?.id === value?.id
                }
                noOptionsText={
                  courseLoading
                    ? "Searching…"
                    : courseDebouncedSearch
                    ? "No matching courses"
                    : "Type to search courses"
                }
                ListboxProps={{
                  style: { maxHeight: "220px" },
                  ...courseListboxProps,
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Course"
                    required
                    helperText="Type to search courses"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {courseLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <div>
                      <div className="font-semibold">{option?.courseName || "Course Name"}</div>
                      <div className="text-sm text-app-text-secondary">
                        {option?.university?.name || "University Name"}
                      </div>
                    </div>
                  </Box>
                )}
              />
              {formData.course && (
                <Autocomplete
                  disablePortal
                  size="small"
                  options={formData.course?.intakes || []}
                  value={formData.intake || null}
                  onChange={(e, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      intake: newValue || "",
                    }));
                  }}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => <TextField {...params} label="Select Intake" required />}
                />
              )}
              {formData.course && (
                <InputField
                  label="Extra Information"
                  placeholder="Enter Extra Information"
                  name="extraInformation"
                  type="text"
                  value={formData.extraInformation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, extraInformation: e.target.value }))
                  }
                />
              )}
            </div>
          </Box>
        );

      case 4:
        return (
          <Box className="space-y-6 py-6">
            <Typography variant="h6" className="mb-4 text-app-text">
              Review Information
            </Typography>
            <div className="space-y-4">
              <Paper elevation={1} className="p-4">
                <Typography variant="subtitle1" className="font-semibold mb-3">
                  Basic Details
                </Typography>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-app-text-secondary">Name:</span>
                    <span className="ml-2 font-semibold">{formData.name}</span>
                  </div>
                  <div>
                    <span className="text-app-text-secondary">Email:</span>
                    <span className="ml-2 font-semibold">{formData.email}</span>
                  </div>
                  <div>
                    <span className="text-app-text-secondary">Phone:</span>
                    <span className="ml-2 font-semibold">{formData.phone}</span>
                  </div>
                  <div>
                    <span className="text-app-text-secondary">Address:</span>
                    <span className="ml-2 font-semibold">{formData.address}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-app-text-secondary">Emergency Contact:</span>
                    <span className="ml-2 font-semibold">
                      {formData.emergencyContact.name} ({formData.emergencyContact.relation}) -{" "}
                      {formData.emergencyContact.phone}
                    </span>
                  </div>
                </div>
              </Paper>

              <Paper elevation={1} className="p-4">
                <Typography variant="subtitle1" className="font-semibold mb-3">
                  Interested Services
                </Typography>
                <div className="flex flex-wrap gap-2">
                  {formData.interestedServices.map((service) => (
                    <span
                      key={service}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </Paper>

              {formData.interestedServices.includes("Admission") && formData.course && (
                <Paper elevation={1} className="p-4">
                  <Typography variant="subtitle1" className="font-semibold mb-3">
                    Course & Intake
                  </Typography>
                  <div className="text-sm">
                    <div>
                      <span className="text-app-text-secondary">Course:</span>
                      <span className="ml-2 font-semibold">{formData.course?.courseName}</span>
                    </div>
                    {formData.university?.name && (
                      <div className="mt-2">
                        <span className="text-app-text-secondary">University:</span>
                        <span className="ml-2 font-semibold">
                          {formData.university?.name || formData.course?.university?.name}
                        </span>
                      </div>
                    )}
                    {formData.intake && (
                      <div className="mt-2">
                        <span className="text-app-text-secondary">Intake:</span>
                        <span className="ml-2 font-semibold">{formData.intake}</span>
                      </div>
                    )}
                    {formData.extraInformation && (
                      <div className="mt-2">
                        <span className="text-app-text-secondary">Extra Information:</span>
                        <span className="ml-2 font-semibold">{formData.extraInformation}</span>
                      </div>
                    )}
                  </div>
                </Paper>
              )}

              {formData.documents && formData.documents.length > 0 && (
                <Paper elevation={1} className="p-4">
                  <Typography variant="subtitle1" className="font-semibold mb-3">
                    Documents ({formData.documents.length} uploaded)
                  </Typography>
                </Paper>
              )}
            </div>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-10 py-3 border-0">
            <div className="flex flex-wrap items-center">
              <div className="relative w-full max-w-full flex justify-start gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className="font-semibold text-xl text-app-text">
                  Add Client - Multi Step Form
                </h3>
              </div>
            </div>
          </div>

          <div className="block w-full overflow-x-auto mt-8">
            <div className="flex-auto lg:px-10 py-10 pt-0">
              <Stepper activeStep={activeStep} alternativeLabel className="mb-8">
                {steps.map((label, index) => {
                  // Skip step 3 if admission not selected
                  if (index === 3 && !formData.interestedServices.includes("Admission")) {
                    return null;
                  }
                  return (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              <Box className="mb-8">{renderStepContent(activeStep)}</Box>

              <Box className="flex justify-between mt-8 pt-6 border-t">
                <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
                  Back
                </Button>
                <Box className="flex gap-4">
                  <Button variant="outlined" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                  {activeStep === steps.length - 1 ||
                  (activeStep === 3 && !formData.interestedServices.includes("Admission")) ? (
                    <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={handleNext}>
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientMultiStepForm;
